import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import SalesWidget from '../components/SalesWidget';
import { 
  ArrowRight, Check, X, MessageSquare, Calendar, Bot, 
  Clock, Shield, Phone, Mail, Star, Users, TrendingUp,
  Zap, Building2, Crown, ChevronDown, ChevronUp, User, Info
} from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const { theme } = useTheme();

  // Theme classes
  const bgMain = theme === 'dark' ? 'bg-[#030712]' : 'bg-white';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-lg';
  const sectionBg = theme === 'dark' ? 'bg-slate-900/50' : 'bg-gray-50';

  const faqs = [
    {
      q: "¿Funciona para mi especialidad?",
      a: "Sí, AI Med Planner funciona para cualquier especialidad médica: oftalmología, dermatología, ginecología, pediatría, cardiología, etc. El asistente se configura específicamente con tus servicios, precios y terminología."
    },
    {
      q: "¿Qué pasa con los datos de mis pacientes?",
      a: "La seguridad es nuestra prioridad. Usamos cifrado AES-256, servidores en México y cumplimos con la LFPDPPP y NOM-024-SSA3-2012. Tus datos y los de tus pacientes están protegidos."
    },
    {
      q: "¿Puedo cancelar cuando quiera?",
      a: "Absolutamente. No hay contratos de permanencia. Puedes cancelar tu suscripción cuando quieras y seguirás teniendo acceso hasta el final del período de facturación."
    },
    {
      q: "¿Necesito saber de tecnología?",
      a: "No. La configuración la hacemos nosotros. Solo necesitas decirnos qué servicios ofreces, tus horarios y precios. En 48 horas tu asistente estará funcionando."
    },
    {
      q: "¿Funciona con mi WhatsApp actual?",
      a: "Necesitas WhatsApp Business API (diferente a WhatsApp Business normal). Te ayudamos a configurarlo sin costo adicional como parte del setup."
    }
  ];

  return (
    <div className={`min-h-screen ${bgMain} ${textPrimary}`}>
      
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Gradient */}
        <div 
          className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-br from-slate-900 via-blue-900/50 to-slate-900' : 'bg-gradient-to-br from-blue-50 via-white to-cyan-50'}`}
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLThoLTJ2LTRoMnY0em0wLThoLTJWMTRoMnY0em0wLThoLTJWNmgydjR6bTAgMjRoLTJ2LTRoMnY0em0wIDhoLTJ2LTRoMnY0em0wIDhoLTJ2LTRoMnY0eiIvPjwvZz48L2c+PC9zdmc+')] opacity-20" />
        </div>
        
        {/* Nav */}
        <nav className="absolute top-0 left-0 right-0 z-20 p-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">AI</span>
              </div>
              <span className={`text-xl font-bold ${textPrimary}`}>Med Planner</span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Button 
                onClick={() => navigate('/login')}
                variant="outline" 
                className={theme === 'dark' ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}
              >
                Iniciar Sesión
              </Button>
            </div>
          </div>
        </nav>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm mb-6">
              <Star className="w-4 h-4 fill-current" />
              Automatización inteligente para clínicas
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Automatice su clínica o consultorio.
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                {" "}Incremente sus citas{" "}
              </span>
              sin contratar más personal.
            </h1>
            
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 flex items-center justify-center">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="font-semibold text-lg">Asistente IA disponible 24/7</p>
                <p className="text-gray-400">Atiende pacientes, agenda citas y califica leads</p>
                <p className="text-gray-500 text-sm">Usado por +50 clínicas en México</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg"
              >
                Empezar Prueba Gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' })}
              >
                <Info className="mr-2 w-5 h-5" />
                Cómo Funciona
              </Button>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* CTA SECTION - BENEFITS */}
      <section className="py-16 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Empieza a recibir más citas en menos de 48 horas
            </h2>
            <p className="text-gray-400">Sin instalaciones complicadas. Sin contratos a largo plazo.</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 mb-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-400">+40%</p>
              <p className="text-gray-500 text-sm">Más citas agendadas</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-400">24/7</p>
              <p className="text-gray-500 text-sm">Atención sin descanso</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-400">30 seg</p>
              <p className="text-gray-500 text-sm">Respuesta promedio</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-400">14 días</p>
              <p className="text-gray-500 text-sm">Prueba gratis</p>
            </div>
          </div>
          
          <div className="flex justify-center">
            <Button 
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg"
            >
              Comenzar Prueba Gratis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12">
            "¿Te pierdes pacientes porque no contestas WhatsApp rápido?"
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {[
              "Recepcionista no da abasto con mensajes",
              "Pacientes se van con la competencia",
              "Nadie atiende fines de semana",
              "Leads de Meta Ads sin seguimiento",
              "Agendas desorganizadas"
            ].map((problem, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <X className="w-6 h-6 text-red-400 flex-shrink-0" />
                <span className="text-gray-300">{problem}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-24 px-6 bg-gradient-to-b from-blue-900/20 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              AI Med Planner: Tu Recepcionista de IA 24/7
            </h2>
            <p className="text-gray-400 text-lg">
              Automatiza la atención de pacientes sin perder el toque humano
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MessageSquare, title: "Responde en segundos", desc: "24/7, incluso fines de semana" },
              { icon: Users, title: "Califica leads", desc: "Automáticamente prioriza urgencias" },
              { icon: Calendar, title: "Agenda citas", desc: "Directo en Google Calendar" },
              { icon: Clock, title: "Envía recordatorios", desc: "Reduce no-shows 40%" },
              { icon: Building2, title: "Multi-especialidad", desc: "Configura múltiples servicios" },
              { icon: Shield, title: "Datos protegidos", desc: "Cumple normativas médicas" }
            ].map((feature, idx) => (
              <div key={idx} className="p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-blue-500/50 transition-colors">
                <feature.icon className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDY */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Resultados reales de nuestros clientes
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              { value: "+40%", label: "Más citas agendadas", color: "text-green-400" },
              { value: "30s", label: "Tiempo respuesta promedio", color: "text-blue-400" },
              { value: "$15K", label: "MXN ahorrados/mes", color: "text-yellow-400" },
              { value: "95%", label: "Satisfacción pacientes", color: "text-purple-400" }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-6 bg-white/5 rounded-2xl border border-white/10">
                <p className={`text-4xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-gray-400 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
          
          {/* Testimonial */}
          <div className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 p-8 md:p-12 rounded-2xl border border-blue-500/20">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center border-4 border-blue-500">
                <User className="w-16 h-16 text-white" />
              </div>
              <div>
                <p className="text-xl md:text-2xl text-gray-200 italic mb-6">
                  "Como médico, necesitaba algo que entendiera términos médicos y urgencias. 
                  AI Med Planner fue diseñado específicamente para clínicas como la mía."
                </p>
                <div>
                  <p className="font-semibold text-lg">Dr. CSG</p>
                  <p className="text-gray-400">Oftalmólogo • CDMX</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="py-24 px-6 bg-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            ¿Cómo funciona?
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", icon: Phone, title: "Conectas WhatsApp", desc: "Te ayudamos a configurar WhatsApp Business API" },
              { step: "2", icon: Zap, title: "Entrenas la IA", desc: "Sube documentos e instrucciones para personalizar" },
              { step: "3", icon: Bot, title: "IA atiende 24/7", desc: "El asistente responde y califica pacientes" },
              { step: "4", icon: Calendar, title: "Citas automáticas", desc: "Las citas aparecen en tu Google Calendar" }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 relative">
                  <item.icon className="w-8 h-8 text-white" />
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-white text-blue-600 rounded-full text-sm font-bold flex items-center justify-center">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Precios para Clínicas Médicas
          </h2>
          <p className="text-gray-400 text-center mb-16">
            14 días de prueba gratis
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Plan Básico */}
            <Card className="bg-white/5 border-white/10 relative overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6">
                  <Zap className="w-10 h-10 text-blue-400 mb-4" />
                  <h3 className="text-xl font-bold">Básico</h3>
                  <p className="text-gray-400 text-sm">Ideal para consultorios pequeños</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold">$2,499</span>
                  <span className="text-gray-400"> MXN/mes</span>
                  <p className="text-gray-500 text-sm">~$129 USD</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {["3 usuarios", "500 conversaciones/mes", "WhatsApp + IA", "Google Calendar", "Soporte email", "Mensajes de voz"].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-4 h-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-white/10 hover:bg-white/20"
                >
                  Empezar Prueba Gratis
                </Button>
              </CardContent>
            </Card>

            {/* Plan Profesional */}
            <Card className="bg-gradient-to-b from-blue-900/50 to-blue-900/20 border-blue-500 relative overflow-hidden scale-105 shadow-2xl shadow-blue-500/20">
              <div className="absolute top-0 left-0 right-0 bg-blue-500 text-center py-2 text-sm font-medium">
                ⭐ MÁS POPULAR
              </div>
              <CardContent className="p-8 pt-14">
                <div className="mb-6">
                  <Crown className="w-10 h-10 text-yellow-400 mb-4" />
                  <h3 className="text-xl font-bold">Profesional</h3>
                  <p className="text-gray-400 text-sm">Para clínicas en crecimiento</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold">$4,999</span>
                  <span className="text-gray-400"> MXN/mes</span>
                  <p className="text-gray-500 text-sm">~$259 USD</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {["10 usuarios", "2,000 conversaciones/mes", "Meta Ads tracking", "Analytics avanzado", "Soporte prioritario", "Mensajes de voz"].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-4 h-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                >
                  Empezar Prueba Gratis
                </Button>
              </CardContent>
            </Card>

            {/* Plan Empresarial */}
            <Card className="bg-white/5 border-white/10 relative overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6">
                  <Building2 className="w-10 h-10 text-orange-400 mb-4" />
                  <h3 className="text-xl font-bold">Empresarial</h3>
                  <p className="text-gray-400 text-sm">Para hospitales y cadenas</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold">$9,999</span>
                  <span className="text-gray-400"> MXN/mes</span>
                  <p className="text-gray-500 text-sm">~$519 USD</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {["25 usuarios", "Conversaciones ilimitadas", "API dedicada", "Account Manager", "SLA garantizado", "Mensajes de voz"].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-4 h-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-white/10 hover:bg-white/20"
                >
                  Contactar Ventas
                </Button>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-8">
            14 días de prueba gratis
          </p>
        </div>
      </section>

      {/* MEDICAL TRUST */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Diseñado por médicos, para médicos
          </h2>
          <p className="text-gray-400 mb-12">
            La seguridad de tus pacientes es nuestra prioridad
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: "🔒", title: "Conexión HTTPS segura" },
              { icon: "🛡️", title: "Datos encriptados" },
              { icon: "👤", title: "Privacidad prioritaria" },
              { icon: "✅", title: "Acceso protegido" }
            ].map((badge, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-xl border border-white/10">
                <span className="text-3xl mb-2 block">{badge.icon}</span>
                <p className="text-sm text-gray-300">{badge.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Preguntas Frecuentes
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full p-6 flex items-center justify-between text-left"
                >
                  <span className="font-medium">{faq.q}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-6 text-gray-400">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">AI</span>
              </div>
              <div>
                <p className="font-bold">AI Med Planner</p>
                <p className="text-gray-500 text-sm">Automatización médica con IA</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-gray-400 text-sm">
              <Link to="/privacy" className="hover:text-white">Privacidad</Link>
              <a href="mailto:contacto@aimedplanner.com" className="hover:text-white flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Contacto
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
            <p>© 2026 AI Med Planner. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
      
      {/* Sales Widget */}
      <SalesWidget />
    </div>
  );
}
